import Styled from 'styled-components/macro';
import {tokensForThemeProvider} from '@forecast-it/design-system';

/**
 * The decision form sits inside the activity card rather than in a modal, so it needs to read as a
 * nested surface. NestedCard carries its own collapse behaviour and a fixed sub-component API, and
 * Card is a different shape, so the chrome is drawn here from the shared tokens.
 */
export const DecisionPanel = Styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokensForThemeProvider.sky.sys.spacing[400]};
	padding: ${tokensForThemeProvider.sky.sys.spacing[400]};
	background-color: ${tokensForThemeProvider.sky.sys.color.background.neutral['2']};
	border: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
	border-radius: ${tokensForThemeProvider.sky.sys.spacing[200]};
`;

/**
 * The reason field laid out label-beside-control rather than label-above.
 *
 * TextArea has no `hideLabel`, so the label cannot be lifted out and placed in a FieldRow the way
 * the contact picker's is — dropping `label` to do that would cost the accessible name and the
 * design system's own "(Optional)" suffix. Instead the wrapper flattens the FlexColumn that
 * InfoDisplayWrapper renders internally, which keeps the component's API and a11y intact. A
 * child selector out-specifies the styled class it overrides, so no !important is needed.
 */
export const ReasonFieldRow = Styled.div`
	> * {
		flex-direction: row;
		align-items: center;
		gap: ${tokensForThemeProvider.sky.sys.spacing[600]};
	}

	/* Sized to the label so it never wraps mid-phrase, matching the contact row above it. */
	> * > :first-child {
		flex: 0 0 auto;
		white-space: nowrap;
	}

	/*
	 * Runs to the panel's right edge, so the control lines up with the buttons beneath it.
	 *
	 * Everything-but-the-label rather than :last-child. InfoDisplayWrapper renders up to four
	 * slots - label row, helperText, children, errorText - and only the first two exist today.
	 * Give the TextArea an errorText or helperText and :last-child would move onto that Text,
	 * handing it the flex the control needs and collapsing the control onto the label row. This
	 * form cannot do that: whatever else appears, the control keeps its sizing.
	 *
	 * Note it would still lay a helper or error line out BESIDE the control rather than under
	 * it, which is not what those slots are designed for - introducing either means revisiting
	 * this row layout, not just this selector.
	 */
	> * > :not(:first-child) {
		flex: 1 1 auto;
		min-width: 0;
	}
`;

/**
 * An emphasised noun inside otherwise muted body copy — "Files" in the revise description, naming
 * the place the current version is filed. Darker than the sentence around it rather than a
 * different hue, so it reads as emphasis and not as a link, which it is not.
 */
export const Emphasis = Styled.span`
	/* The label token differs from the surrounding body token by weight alone — same family, size
	   and line-height — so this emphasises without disturbing the line it sits in. */
	${tokensForThemeProvider.sky.sys.font.label['1']};
	color: ${tokensForThemeProvider.sky.sys.color.text.content.main};
`;

/** Label column and control, so the control keeps a stable left edge across both decisions. */
export const FieldRow = Styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${tokensForThemeProvider.sky.sys.spacing[600]};

	/* Sized to the label so it never wraps mid-phrase, with the gap doing the separating. */
	> :first-child {
		flex: 0 0 auto;
		white-space: nowrap;
	}

	/* Runs to the panel's right edge, so the control lines up with the buttons beneath it. */
	> :last-child {
		flex: 1 1 auto;
		min-width: 0;
	}
`;
